import type {Address, AddressFormValidation, Continent, Country} from "~/types/Address";
import {type BasicVehicle, createDefaultBasicVehicle, type ValidationBasicVehicleStatus} from "~/types/Vehicle";
import {createDefaultAddress, createDefaultAddressFormValidation} from "~/types/Address";
import type {RcaDriverFormValidation} from "~/types/Customer";

export interface Purpose {
    id: number | null;
    code: string | null;
    name: string | null;
}

export interface ApiPurpose {
    id: number | null;
    value: string | null;
    code: string | null;
}

export interface TransportationMethod {
    id: number | null;
    code: string | null;
    name: string | null;
}

export interface ApiTransportationMethod {
    id: number | null;
    value: string | null;
    code: string | null;
}

export interface DocumentType {
    id: number | null;
    code: string | null;
    name: string | null;
}

export interface ApiDocumentType {
    id: number | null;
    value: string | null;
    code: string | null;
}

export interface AmountInsured {
    id: number | null;
    name: string | null;
}

export interface ApiAmountInsured {
    id: number | null;
    value: string | null;
}

export interface CoverageList {
    is_road_assistance_coverage_included: boolean | null;
}

export interface TravelUser {
    first_name: string | null;
    last_name: string | null;
    cnp: string | null;
    document_type: DocumentType | null;
    series: string | null;
    number: number | null;
    address: Address;
}

export interface TravelUsersFormValidation {
    [key: string]: TravelUserFormValidation | null;
}

export interface TravelUserFormValidation {
    first_name: boolean;
    last_name: boolean;
    cnp: boolean;
    document_type: boolean;
    series: boolean;
    number: boolean;
    address: AddressFormValidation;
    [key: string]: boolean | AddressFormValidation;
}

export const createDefaultTravelUserFormValidation = (): TravelUserFormValidation => {
    return {
        first_name: false,
        last_name: false,
        cnp: false,
        document_type: false,
        series: false,
        number: false,
        address: createDefaultAddressFormValidation(),
    }
}

export interface TravelForm {
    continent: Continent | null;
    country: Country | null;
    purpose: Purpose | null;
    transportation_method: TransportationMethod | null;
    amount_insured: AmountInsured | null;
    valid_from: string | null;
    valid_until: string | null;
    number_of_days: number | null;
    coverage_list: CoverageList | null;
    number_of_travel_users: number | null;
    travel_users: Array<TravelUser>;
    vehicle: BasicVehicle | null;
    email: string | null;
    phone_number: string | null;
    token: string | null | undefined;
    amount_insured_key: number | null;
    gdpr_agreement: boolean;
    [key: string]: Continent | Country | Purpose | TransportationMethod | DocumentType | AmountInsured | CoverageList | TravelUser[] | BasicVehicle | string | number | undefined | null | boolean;
}

export interface ValidationTravelStatus {
    continent: boolean;
    country: boolean;
    purpose: boolean;
    transportation_method: boolean;
    amount_insured: boolean;
    valid_from: boolean;
    valid_until: boolean;
    number_of_days: boolean;
    coverage_list: boolean;
    number_of_travel_users: boolean;
    travel_users: ValidationTravelUserStatus[];
    vehicle: ValidationBasicVehicleStatus;
    email: boolean;
    phone_number: boolean;
    gdpr_agreement: boolean;

    [key: string]: boolean | boolean[] | ValidationTravelUserStatus[] | ValidationBasicVehicleStatus;
}

export const createDefaultValidationTravelStatus = (): ValidationTravelStatus => {
    return {
        continent: false,
        country: false,
        purpose: false,
        transportation_method: false,
        amount_insured: true,
        valid_from: true,
        valid_until: false,
        number_of_days: false,
        coverage_list: true,
        number_of_travel_users: true,
        travel_users: [
            {
                first_name: false,
                last_name: false,
                cnp: false,
                document_type: false,
                series: false,
                number: false,
                address: false,
            }
        ],
        vehicle: {
            rca_mapped_category: true,
            vehicle_type: true,
            registration_plate: false,
            vin: false,
            make: false,
            model: false,
            number_of_seats: false,
            max_weight: false,
            manufacture_year: false,
        },
        email: false,
        phone_number: false,
        gdpr_agreement: false,
    }
}

export interface ValidationTravelUserStatus {
    first_name: boolean;
    last_name: boolean;
    cnp: boolean;
    document_type: boolean;
    series: boolean;
    number: boolean;
    address: boolean;

    [key: string]: boolean | boolean[];
}

export const createDefaultTravelForm = (): TravelForm => {
    return {
        continent: {
            id: 1,
            code: "EU",
            name: "Europa"
        },
        country: null,
        purpose: {
            id: 1,
            code: "travel",
            name: "Turism"
        },
        transportation_method: {
            id: 1,
            code: "plane",
            name: "Avion"
        },
        amount_insured: {id: null, name: null},
        valid_from: null,
        valid_until: null,
        number_of_days: null,
        coverage_list: {
            is_road_assistance_coverage_included: false
        },
        number_of_travel_users: 1,
        travel_users: [
            {
                first_name: null,
                last_name: null,
                cnp: null,
                document_type: {
                    id: 1,
                    code: "CI",
                    name: "Buletin"
                },
                series: null,
                number: null,
                address: createDefaultAddress(),
            },
        ],
        token: null,
        vehicle: createDefaultBasicVehicle(),
        email: null,
        phone_number: null,
        amount_insured_key: 3, // 50k
        gdpr_agreement: false,
    };
};

export interface TravelInsuranceCompany {
    id: number | null;
    name: string | null;
    code: string | null;
    logo: string | null;
}

export interface ApiTravelDetail {
    id: number | null;
    valid_from: string | null;
    valid_until: string | null;
    duration: number | null;
    purpose: string | null;
    transportation_method: string | null;
    request_body: {
        continent: Continent | null;
        country: Country | null;
        purpose: Purpose | null;
        transportation_method: TransportationMethod | null;
        amount_insured: AmountInsured | null;
        valid_from: string | null;
        valid_until: string | null;
        number_of_days: number | null;
        coverage_list: {
            is_road_assistance_coverage_included: boolean | null;
        };
        number_of_travel_users: number | null;
        travel_users: Array<TravelUser>;
        token: string | null;
        vehicle: BasicVehicle | null;
        email: string | null;
        amount_insured_key: number | null;
        country_destination: Country | null;
    };
    group_type: string | null;
    area_of_validity: string | null;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
    country_destination_id: number | null;
    user_id: number | null;
    vehicle_id: number | null;
}

export interface TravelOffer {
    [key: string]: any;
}
